/**
 * DemoScreen — "Try demo" experience that lets a visitor experience the
 * full Live translation flow without committing to an account.
 *
 * Lifecycle:
 *   1. AuthScreen's "Try demo" button provisions a throwaway anonymous
 *      account via `/auth/signup` and stashes the JWT pair in
 *      {@link authStore.setDemoTokens} (in-memory, NOT localStorage).
 *   2. {@link DemoScreen} mounts and walks the user through three steps:
 *      enroll voice → pick speaker language → pick listener language → live.
 *   3. In the live step we render the regular {@link LiveScreen} with the
 *      `demoMode` prop wired up. The real ASR/MT/TTS pipeline handles the
 *      speaker; a canned-phrase bank + browser SpeechSynthesis fake the
 *      listener so the user sees a believable round trip.
 *   4. On exit, the anon account is deleted via `DELETE /auth/account` and
 *      the store is cleared so the visitor lands back on AuthScreen.
 *
 * The screen renders OUTSIDE the normal authenticated app shell (no sidebar,
 * no bottom nav) so the demo feels like its own focused product surface.
 */
import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import * as api from "../api";
import { authStore } from "../store";
import { Btn, IconBtn, useLanguages, langByCode } from "../ui";
import { Icon } from "../icons";
import { Wordmark } from "../logo";
import { LiveScreen, type LiveDemoMode } from "./LiveScreen";
import { EnrollmentWizard } from "./EnrollScreen";

type Step = "speaker" | "enroll" | "listener" | "live" | "claim";
/** Why the user landed on the claim step. Influences the headline copy. */
type ClaimReason = "manual" | "limit";

/**
 * Hard cap on user-initiated turns inside the demo before we nudge the
 * visitor to create a real account. One "turn" = one finalised owner
 * utterance (which also kicks off one AI guest reply), so 50 covers a
 * generous round-trip session without the demo morphing into the real
 * product for free.
 */
const DEMO_TURN_LIMIT = 50;

const STEPS: { id: Step; label: string }[] = [
  { id: "speaker",  label: "You" },
  { id: "enroll",   label: "Voice" },
  { id: "listener", label: "Listener" },
  { id: "live",     label: "Demo" },
];

export function DemoScreen({
  theme, toggleTheme,
}: {
  theme: "light" | "dark"; toggleTheme: () => void;
}) {
  const user = authStore((s) => s.user);
  const [step, setStep] = useState<Step>("speaker");
  const [exiting, setExiting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** Finalised owner turns inside the live step. Used to enforce {@link DEMO_TURN_LIMIT}. */
  const [turnCount, setTurnCount] = useState(0);
  /** Why the claim step was shown — banner copy differs slightly. */
  const [claimReason, setClaimReason] = useState<ClaimReason>("manual");
  // Gender of the AI stand-in's TTS voice. Picked on the listener step;
  // forwarded into LiveScreen → /demo/reply so server-side TTS uses the
  // matching Azure neural voice.
  const [voiceGender, setVoiceGender] = useState<"female" | "male">("female");
  // Gender of the SPEAKER's TTS voice — the voice used when the speaker's
  // utterance is translated into the listener's language and played back
  // through the WS pipeline. Persisted to the throwaway account via
  // PATCH /me on the speaker step so the server picks it up at session
  // start. (The demo signup hardcodes "female" — we override here.)
  const [speakerGender, setSpeakerGender] = useState<"female" | "male">(
    user?.tts_voice_gender ?? "female",
  );

  // Default codes if /me hasn't loaded yet.
  const speakerCode = user?.owner_lang ?? "en-US";
  const listenerCode = user?.other_lang_override ?? user?.fallback_other_lang ?? "hi-IN";

  async function exitDemo() {
    if (exiting) return;
    setExiting(true);
    // Best-effort account cleanup; ignore failures so a flaky network
    // doesn't strand the user in a half-broken state.
    try { await api.deleteAccount(); } catch { /* ignore */ }
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    authStore.getState().logout();
  }

  /**
   * Stop the live session and surface the claim/discard prompt. Used by
   * both the banner "End demo" button (reason="manual") and the turn-cap
   * watcher (reason="limit"). Switching `step` unmounts LiveScreen which
   * releases the mic + closes the WS cleanly.
   */
  function requestExit(reason: ClaimReason) {
    if (step === "claim") return;
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    setClaimReason(reason);
    setStep("claim");
  }

  // Auto-trip the claim prompt once the demo has eaten its turn budget.
  // Guarded by `step === "live"` so we don't re-fire after the user has
  // already moved on to the claim screen.
  useEffect(() => {
    if (step === "live" && turnCount >= DEMO_TURN_LIMIT) {
      requestExit("limit");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, turnCount]);

  async function setSpeakerLang(code: string) {
    try {
      const me = await api.patchMe({ owner_lang: code, tts_voice_gender: speakerGender });
      authStore.getState().setUser(me);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }
  async function setListenerLang(code: string) {
    try {
      const me = await api.patchMe({ other_lang_override: code });
      authStore.getState().setUser(me);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  // The live step takes over the whole viewport (LiveScreen owns its own
  // layout). For the wizard steps we render a centered card with a banner.
  if (step === "live") {
    const demoMode: LiveDemoMode = { speakerCode, listenerCode, voiceGender };
    return (
      <div style={{ height: "100dvh", overflow: "hidden", background: "var(--bg-sunken)", display: "flex", flexDirection: "column" }}>
        <DemoBanner step="live" onExit={() => requestExit("manual")} exiting={exiting}
          theme={theme} toggleTheme={toggleTheme}
          turnCount={turnCount} turnLimit={DEMO_TURN_LIMIT} />
        <AiGuestNotice />
        <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <LiveScreen go={() => { /* in demo, nav is locked */ }} hasActive demoMode={demoMode}
            onFinalTurn={() => setTurnCount((c) => c + 1)} />
        </div>
      </div>
    );
  }

  if (step === "claim") {
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg-sunken)", display: "flex", flexDirection: "column" }}>
        <DemoBanner step="claim" onExit={exitDemo} exiting={exiting}
          theme={theme} toggleTheme={toggleTheme}
          turnCount={turnCount} turnLimit={DEMO_TURN_LIMIT} />
        <ClaimAccountStep
          reason={claimReason}
          turnCount={turnCount}
          turnLimit={DEMO_TURN_LIMIT}
          onDiscard={exitDemo}
          discarding={exiting}
        />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-sunken)", display: "flex", flexDirection: "column" }}>
      <DemoBanner step={step} onExit={exitDemo} exiting={exiting}
        theme={theme} toggleTheme={toggleTheme}
        turnCount={turnCount} turnLimit={DEMO_TURN_LIMIT} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {err && (
          <div style={{
            margin: "12px auto 0", maxWidth: 560, width: "calc(100% - 32px)",
            background: "color-mix(in oklch, var(--bad) 12%, transparent)",
            color: "var(--bad)", padding: "10px 14px", borderRadius: 12, fontSize: 13.5,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <Icon name="shield" size={16} /> {err}
            <button onClick={() => setErr(null)} style={{
              marginLeft: "auto", background: "transparent", border: "none", cursor: "pointer", color: "var(--bad)",
              display: "flex", padding: 2,
            }}><Icon name="x" size={16} /></button>
          </div>
        )}
        {step === "speaker" && (
          <LangStep
            title="Which language will you speak?"
            subtitle="We'll use this for your voice enrollment and during the demo. In the real product you'd set your phone between the two of you — the other person speaks their own language into the same device. Here we'll spin up an AI stand-in to play their part."
            currentCode={speakerCode}
            who="you"
            onPick={async (code) => { await setSpeakerLang(code); }}
            onNext={() => setStep("enroll")}
            onBack={exitDemo}
            backLabel="Cancel"
            // Restrict to languages we ship enrollment prompt packs for —
            // otherwise the user picks Kannada/Telugu/etc. and the wizard
            // shows English fallback text.
            filter={(l) => l.enrollPrompts === true}
            extras={
              <GenderPicker
                label="Your voice"
                value={speakerGender}
                onChange={setSpeakerGender}
              />
            }
          />
        )}
        {step === "enroll" && (
          <EnrollmentWizard
            onCancel={() => setStep("speaker")}
            onDone={() => setStep("listener")}
            targetSecOverride={10}
            lockedLang={speakerCode}
          />
        )}
        {step === "listener" && (
          <LangStep
            title="What language should the AI stand-in speak?"
            subtitle="Just for this demo, an AI plays the other speaker so you can try the flow alone. They'll reply in this language. In the real Aevum app this would be an actual person speaking into the same phone as you — we'd hear their voice first, transcribe it, then translate. To keep the demo snappy we skip that step and jump straight to the translated reply."
            currentCode={listenerCode}
            who="guest"
            onPick={async (code) => { await setListenerLang(code); }}
            onNext={() => setStep("live")}
            onBack={() => setStep("enroll")}
            nextLabel="Start demo"
            nextIcon="mic"
            extras={
              <GenderPicker
                label="AI stand-in voice"
                value={voiceGender}
                onChange={setVoiceGender}
              />
            }
          />
        )}
      </div>
    </div>
  );
}

function DemoBanner({
  step, onExit, exiting, theme, toggleTheme, turnCount, turnLimit,
}: {
  step: Step; onExit: () => void; exiting: boolean;
  theme: "light" | "dark"; toggleTheme: () => void;
  /** Demo turn progress — only rendered while the user is on the live step. */
  turnCount: number; turnLimit: number;
}) {
  const idx = STEPS.findIndex((s) => s.id === step);
  // Claim is a terminal step — highlight "Demo" as the most-recent active
  // chip so the progress bar doesn't visually "reset".
  const effectiveIdx = step === "claim" ? STEPS.length - 1 : idx;
  const remaining = Math.max(0, turnLimit - turnCount);
  const showRemaining = step === "live";
  return (
    <header style={{
      flexShrink: 0, padding: "14px 24px",
      background: "var(--surface)", borderBottom: "1px solid var(--border)",
      display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
    }}>
      <Wordmark size={22} />
      <span style={{
        fontSize: 11, letterSpacing: 1.4, color: "var(--accent)",
        background: "var(--accent-soft)", padding: "3px 10px",
        borderRadius: 999, fontWeight: 700, textTransform: "uppercase",
      }} className="mono">Demo</span>
      {/* Stepper. order:99 + width:100% on wrap moves it onto its own row
          when the viewport is too narrow to fit [Wordmark · Demo · Steps ·
          turns-left · theme · End] on one line — without this it crashes
          into the theme toggle. */}
      <div style={{
        flex: "1 1 320px", minWidth: 0,
        display: "flex", alignItems: "center", gap: 10, maxWidth: 540,
      }}>
        {STEPS.map((s, i) => (
          <Stepper key={s.id} label={s.label} active={i === effectiveIdx} done={i < effectiveIdx} last={i === STEPS.length - 1} />
        ))}
      </div>
      {showRemaining && (
        <span className="mono" style={{
          fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3,
          color: remaining <= 5 ? "var(--bad)" : "var(--ink-2)",
          background: remaining <= 5
            ? "color-mix(in oklch, var(--bad) 12%, transparent)"
            : "var(--bg-sunken)",
          padding: "4px 10px", borderRadius: 999,
        }}>
          {remaining} turn{remaining === 1 ? "" : "s"} left
        </span>
      )}
      <IconBtn name={theme === "dark" ? "sun" : "moon"} onClick={toggleTheme} variant="surface" size={36} ic={17} />
      <Btn size="sm" variant="outline" onClick={onExit} disabled={exiting}>
        {exiting ? "Exiting…" : step === "live" ? "End demo" : "Exit demo"}
      </Btn>
    </header>
  );
}

/**
 * Persistent banner shown above the live conversation in demo mode. The
 * "other speaker" in the bubbles is an AI stand-in, not a real human — this
 * stripe keeps that context one glance away so the user never confuses what
 * they're seeing with the real product (one shared phone between two people).
 */
function AiGuestNotice() {
  return (
    <div style={{
      flexShrink: 0, padding: "10px 20px",
      background: "var(--accent-soft)",
      borderBottom: "1px solid var(--border)",
      color: "var(--accent-ink)",
      display: "flex", alignItems: "center", gap: 10,
      fontSize: 13, fontWeight: 600, fontFamily: "var(--font-ui)",
    }}>
      <Icon name="sparkle" size={16} stroke={2.1} />
      <span style={{ flex: 1, minWidth: 0 }}>
        Heads up: the other speaker in this demo is an <strong>AI stand-in</strong>.
        <span style={{ fontWeight: 500, opacity: 0.85 }}>
          {" "}In the real Aevum app, the other person would speak their language into the same phone as you and we'd translate it live. We skip that step here so you can try the flow solo.
        </span>
      </span>
    </div>
  );
}

function Stepper({ label, active, done, last }: { label: string; active: boolean; done: boolean; last: boolean }) {
  const dotColor = done ? "var(--good)" : active ? "var(--accent)" : "var(--border-strong)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flex: last ? "0 0 auto" : 1, minWidth: 0 }}>
      <span style={{
        width: 22, height: 22, borderRadius: "50%",
        display: "grid", placeItems: "center", flexShrink: 0,
        background: done ? dotColor : "transparent",
        border: `1.5px solid ${dotColor}`, color: done ? "var(--on-accent)" : dotColor,
      }}>
        {done ? <Icon name="check" size={12} stroke={2.5} /> : null}
      </span>
      <span style={{
        fontSize: 12.5, fontWeight: active ? 700 : 600,
        color: active ? "var(--ink)" : done ? "var(--ink-2)" : "var(--ink-faint)",
        whiteSpace: "nowrap",
      }}>{label}</span>
      {!last && <span style={{ flex: 1, height: 1.5, background: done ? "var(--good)" : "var(--border)", minWidth: 12 }} />}
    </div>
  );
}

function LangStep({
  title, subtitle, currentCode, who, onPick, onNext, onBack,
  nextLabel = "Next", nextIcon = "arrowRight", backLabel = "Back",
  extras, filter,
}: {
  title: string; subtitle: string;
  currentCode: string; who: "you" | "guest";
  onPick: (code: string) => Promise<void>;
  onNext: () => void; onBack: () => void;
  nextLabel?: string; nextIcon?: string; backLabel?: string;
  extras?: ReactNode;
  /** Optional predicate to restrict the displayed language list. Used
   *  by the speaker step to hide languages without an enrollment
   *  prompt pack — otherwise the user picks Kannada and reads
   *  English fallback prompts during enrollment. */
  filter?: (l: { code: string; enrollPrompts?: boolean }) => boolean;
}) {
  const allLangs = useLanguages();
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(currentCode);
  const [query, setQuery] = useState("");
  // Apply the filter but always keep the currently-picked code visible so
  // the picker can render a selected state even if the filter would have
  // excluded it (e.g. server hasn't hydrated `enrollPrompts` yet).
  const langs = filter
    ? allLangs.filter((l) => l.code === picked || filter(l))
    : allLangs;

  // Free-text search across English name, native name, BCP-47 code, and the
  // short badge so a visitor can quickly find their language in a long list.
  const q = query.trim().toLowerCase();
  const visibleLangs = q
    ? langs.filter((l) =>
        l.name.toLowerCase().includes(q) ||
        l.native.toLowerCase().includes(q) ||
        l.code.toLowerCase().includes(q) ||
        l.short.toLowerCase().includes(q))
    : langs;

  async function commitAndNext() {
    setBusy(true);
    try { await onPick(picked); onNext(); } finally { setBusy(false); }
  }

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: "44px 28px 60px", width: "100%" }}>
      <h1 className="display" style={{
        fontSize: 28, fontWeight: 700, color: "var(--ink)", margin: 0, letterSpacing: -0.5,
      }}>{title}</h1>
      <p style={{ color: "var(--ink-2)", marginTop: 12, fontSize: 15, lineHeight: 1.55 }}>
        {subtitle}
      </p>

      <div style={{
        marginTop: 28, background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: "var(--r-lg)", padding: 18,
      }}>
        <div style={{ fontSize: 12.5, color: "var(--ink-3)", fontWeight: 600, marginBottom: 10 }}>
          Selected: <span style={{ color: who === "you" ? "var(--you)" : "var(--guest)" }}>{langByCode(picked, langs).name}</span>
        </div>
        <div style={{ position: "relative", marginBottom: 10 }}>
          <span style={{
            position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)",
            color: "var(--ink-3)", display: "flex", pointerEvents: "none",
          }}>
            <Icon name="search" size={16} />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search languages…"
            aria-label="Search languages"
            style={{
              width: "100%", height: 40, padding: "0 34px 0 34px", fontSize: 14,
              color: "var(--ink)", background: "var(--surface-2)",
              border: "1px solid var(--border)", borderRadius: "var(--r-md)",
              fontFamily: "var(--font-ui)", outline: "none", boxSizing: "border-box",
            }}
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} aria-label="Clear search" style={{
              position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
              background: "transparent", border: "none", cursor: "pointer",
              color: "var(--ink-3)", display: "flex", padding: 4,
            }}><Icon name="x" size={15} /></button>
          )}
        </div>
        <div style={{
          maxHeight: 360, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4,
        }} className="hc-scroll">
          {visibleLangs.length === 0 && (
            <div style={{ padding: "16px 12px", color: "var(--ink-3)", fontSize: 14, textAlign: "center" }}>
              No languages match &ldquo;{query}&rdquo;.
            </div>
          )}
          {visibleLangs.map((l) => {
            const active = l.code === picked;
            return (
              <button key={l.code} onClick={() => setPicked(l.code)} style={{
                display: "flex", alignItems: "center", gap: 12, padding: "11px 12px",
                background: active ? (who === "you" ? "color-mix(in oklch, var(--you) 14%, transparent)" : "color-mix(in oklch, var(--guest) 14%, transparent)") : "transparent",
                border: "none", borderRadius: 12, cursor: "pointer", textAlign: "left",
                color: "var(--ink)", fontFamily: "var(--font-ui)",
              }}>
                <span className="mono" style={{
                  minWidth: 38, fontSize: 11, fontWeight: 700, padding: "3px 6px",
                  borderRadius: 6, textAlign: "center",
                  background: active ? (who === "you" ? "var(--you)" : "var(--guest)") : "var(--surface-2)",
                  color: active ? "var(--on-accent)" : "var(--ink-3)",
                  letterSpacing: 0.4,
                }}>{l.short}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: active ? 700 : 500 }}>
                  {l.name} <span style={{ color: "var(--ink-3)", fontWeight: 500 }}>· {l.native}</span>
                </span>
                {active && <Icon name="check" size={16} color={who === "you" ? "var(--you)" : "var(--guest)"} stroke={2.5} />}
              </button>
            );
          })}
        </div>
      </div>

      {extras && <div style={{ marginTop: 16 }}>{extras}</div>}

      <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", gap: 12 }}>
        <Btn variant="outline" onClick={onBack} disabled={busy}>{backLabel}</Btn>
        <Btn icon={nextIcon} onClick={commitAndNext} disabled={busy}>
          {busy ? "Saving…" : nextLabel}
        </Btn>
      </div>
    </div>
  );
}

function GenderPicker({
  value, onChange, label = "AI stand-in voice",
}: {
  value: "female" | "male";
  onChange: (v: "female" | "male") => void;
  label?: string;
}) {
  const options: { id: "female" | "male"; label: string }[] = [
    { id: "female", label: "Female voice" },
    { id: "male",   label: "Male voice"   },
  ];
  return (
    <div style={{
      background: "var(--surface)", border: "1px solid var(--border)",
      borderRadius: "var(--r-lg)", padding: 16,
    }}>
      <div style={{ fontSize: 12.5, color: "var(--ink-3)", fontWeight: 600, marginBottom: 10 }}>
        {label}
      </div>
      <div role="radiogroup" aria-label={label} style={{ display: "flex", gap: 8 }}>
        {options.map((o) => {
          const active = o.id === value;
          return (
            <button
              key={o.id}
              role="radio"
              aria-checked={active}
              onClick={() => onChange(o.id)}
              style={{
                flex: 1, padding: "11px 12px", borderRadius: 12, cursor: "pointer",
                border: "1px solid " + (active ? "var(--guest)" : "var(--border)"),
                background: active
                  ? "color-mix(in oklch, var(--guest) 14%, transparent)"
                  : "transparent",
                color: "var(--ink)", fontFamily: "var(--font-ui)",
                fontSize: 14, fontWeight: active ? 700 : 500,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {active && <Icon name="check" size={14} color="var(--guest)" stroke={2.5} />}
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Terminal step shown when the visitor ends the demo (either manually via
 * the banner button or by hitting {@link DEMO_TURN_LIMIT}). Offers two
 * paths:
 *
 *   1. **Create account** — calls `/auth/claim-demo` which upgrades the
 *      throwaway user row in place, preserving the voice enrollment they
 *      just recorded. On success we drop the `isDemo` flag and the app
 *      root re-renders into the normal signed-in shell.
 *   2. **Exit and discard** — fires the original `exitDemo` flow which
 *      deletes the anon account and lands the visitor back on
 *      AuthScreen.
 *
 * The form fields mirror the regular signup form (email, password,
 * display name); enrollment / language prefs were already captured
 * during the wizard so we don't ask twice.
 */
function ClaimAccountStep({
  reason, turnCount, turnLimit, onDiscard, discarding,
}: {
  reason: ClaimReason;
  turnCount: number;
  turnLimit: number;
  onDiscard: () => void;
  discarding: boolean;
}) {
  const cachedUser = authStore((s) => s.user);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState(
    cachedUser?.display_name && !cachedUser.display_name.toLowerCase().startsWith("demo")
      ? cachedUser.display_name
      : "",
  );
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!email || !password || !displayName) {
      setErr("Please fill in name, email, and password.");
      return;
    }
    if (password.length < 12) {
      setErr("Password must be at least 12 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const tokens = await api.claimDemo({
        email: email.trim().toLowerCase(),
        password,
        display_name: displayName.trim(),
      });
      // Flip out of demo mode FIRST so `setTokens` persists to localStorage
      // (the demo-aware setter would otherwise drop the rotated pair).
      authStore.getState().setDemo(false);
      authStore.getState().setTokens(tokens);
      try {
        const me = await api.getMe();
        authStore.getState().setUser(me);
      } catch {
        // Non-fatal: App.tsx re-fetches /me on next mount.
      }
      // App.tsx re-renders into the signed-in shell because `isDemo` is now false.
    } catch (e) {
      setSubmitting(false);
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  const headline = reason === "limit"
    ? "You've hit the demo turn limit"
    : "Save your demo before you go?";
  const subhead = reason === "limit"
    ? `That was ${turnCount} of ${turnLimit} free turns. Create an account in a few seconds to keep your voice enrollment and keep talking — no limits.`
    : "Create an account to keep your voice enrollment and pick up right where you left off — your enrolled voice stays attached to the new account.";

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <div style={{ maxWidth: 520, margin: "0 auto", padding: "44px 28px 60px", width: "100%" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: reason === "limit"
            ? "color-mix(in oklch, var(--bad) 12%, transparent)"
            : "var(--accent-soft)",
          color: reason === "limit" ? "var(--bad)" : "var(--accent)",
          padding: "6px 12px", borderRadius: 999,
          fontSize: 11.5, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase",
        }} className="mono">
          <Icon name={reason === "limit" ? "shield" : "sparkle"} size={14} stroke={2.2} />
          {reason === "limit" ? "Demo limit reached" : "Wrapping up"}
        </div>
        <h1 className="display" style={{
          fontSize: 28, fontWeight: 700, color: "var(--ink)",
          margin: "16px 0 10px", letterSpacing: -0.5,
        }}>{headline}</h1>
        <p style={{ color: "var(--ink-2)", fontSize: 15, lineHeight: 1.55, margin: 0 }}>
          {subhead}
        </p>

        <form onSubmit={submit} style={{
          marginTop: 28, background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: "var(--r-lg)", padding: 22, display: "flex", flexDirection: "column", gap: 14,
        }}>
          <Field label="Your name">
            <input
              autoFocus
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="What should we call you?"
              autoComplete="name"
              style={inputStyle}
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              style={inputStyle}
            />
          </Field>
          <Field label="Password">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 12 characters"
              autoComplete="new-password"
              minLength={12}
              style={inputStyle}
            />
          </Field>
          {err && (
            <div style={{
              background: "color-mix(in oklch, var(--bad) 12%, transparent)",
              color: "var(--bad)", padding: "10px 14px", borderRadius: 12,
              fontSize: 13.5, display: "flex", alignItems: "center", gap: 8,
            }}>
              <Icon name="shield" size={16} /> {err}
            </div>
          )}
          <Btn
            type="submit"
            variant="primary"
            size="lg"
            disabled={submitting || discarding}
            iconRight="arrowRight"
          >
            {submitting ? "Creating account…" : "Create account & keep my voice"}
          </Btn>
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            color: "var(--ink-3)", fontSize: 12.5, justifyContent: "center",
          }}>
            <Icon name="shield" size={13} stroke={2} />
            We never share your audio. Enrollment data stays with your account.
          </div>
        </form>

        <div style={{
          marginTop: 22, display: "flex", justifyContent: "center",
        }}>
          <button
            type="button"
            onClick={onDiscard}
            disabled={discarding || submitting}
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: "var(--ink-3)", fontSize: 13.5, padding: "8px 12px",
              textDecoration: "underline", fontFamily: "var(--font-ui)",
              opacity: discarding || submitting ? 0.5 : 1,
            }}
          >
            {discarding ? "Discarding…" : "No thanks, discard demo data and exit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)" }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: CSSProperties = {
  width: "100%", padding: "11px 14px", borderRadius: 12,
  border: "1px solid var(--border)", background: "var(--bg-sunken)",
  color: "var(--ink)", fontSize: 15, fontFamily: "var(--font-ui)",
  outline: "none",
};
